/**
 * Sentry Client Configuration
 * Captures browser-side errors and sends them to GlitchTip
 *
 * GlitchTip is a self-hosted, Sentry-compatible error tracking service.
 * This config uses the @sentry/nextjs SDK which works seamlessly with GlitchTip.
 */

import * as Sentry from '@sentry/nextjs';
import { SENTRY_DSN, ENVIRONMENT, RELEASE } from './sentry.shared';

// Patterns to identify sensitive data in URLs and breadcrumbs
const SENSITIVE_PATTERNS = [
  /token[=:]\s*["']?[\w-]+/gi,
  /password[=:]\s*["']?[^&\s]+/gi,
  /auth[=:]\s*["']?[\w-]+/gi,
  /bearer\s+[\w-]+/gi,
  /api[_-]?key[=:]\s*["']?[\w-]+/gi,
  /secret[=:]\s*["']?[\w-]+/gi,
];

/**
 * Redacts sensitive information from a string
 */
function redactSensitiveData(input: string): string {
  let result = input;
  for (const pattern of SENSITIVE_PATTERNS) {
    result = result.replace(pattern, '[REDACTED]');
  }
  return result;
}

/**
 * Checks if the error should be ignored (noise reduction)
 */
function shouldIgnoreError(event: Sentry.ErrorEvent): boolean {
  const message = event.exception?.values?.[0]?.value || '';

  // Ignore common browser extension errors
  const ignoredPatterns = [
    /extensions\//i,
    /chrome-extension/i,
    /moz-extension/i,
    /ResizeObserver loop/i,
    /Loading chunk \d+ failed/i, // Handled by Next.js
    /Network request failed/i, // Usually user connectivity issues
  ];

  return ignoredPatterns.some((pattern) => pattern.test(message));
}

Sentry.init({
  dsn: SENTRY_DSN,
  environment: ENVIRONMENT,
  release: RELEASE,

  // Enable when DSN is set. In production, always set DSN.
  // In development, only set DSN in .env.local if you want to test error reporting.
  enabled: !!SENTRY_DSN,

  // Sample 100% of errors in production (adjust if traffic is high)
  sampleRate: 1.0,

  // Disable performance monitoring (tracing) to reduce noise
  // Enable if you need APM: tracesSampleRate: 0.1
  tracesSampleRate: 0,

  // Disable session replay (not supported by GlitchTip)
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,

  // Privacy: Don't send default PII (IP addresses, etc.)
  sendDefaultPii: false,

  // Limit breadcrumb history
  maxBreadcrumbs: 30,

  // Filter and sanitize events before sending
  beforeSend(event) {
    // Skip ignored errors
    if (shouldIgnoreError(event)) {
      return null;
    }

    // Add extra debug context for hydration errors
    const errorMessage = event.exception?.values?.[0]?.value || '';
    if (/Minified React error #4(18|19|21|22|23|25)|hydrat/i.test(errorMessage)) {
      const nextBuildId = (window as typeof window & { __NEXT_DATA__?: { buildId?: string } })
        .__NEXT_DATA__?.buildId;
      const publicBuildId = process.env.NEXT_PUBLIC_BUILD_ID;
      let timeZone = 'unknown';
      let locale = 'unknown';
      let timeZoneOffsetMinutes = 'unknown';
      try {
        timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown';
        locale = navigator.language || 'unknown';
        timeZoneOffsetMinutes = String(new Date().getTimezoneOffset());
      } catch {
        // Ignore timezone/locale access errors
      }
      const localStorageFlags: Record<string, string> = {};
      try {
        localStorageFlags.naka_sidebar_collapsed =
          localStorage.getItem('naka_sidebar_collapsed') ?? 'missing';
        localStorageFlags.naka_sidebar_sections_collapsed =
          localStorage.getItem('naka_sidebar_sections_collapsed') ?? 'missing';
      } catch {
        localStorageFlags.naka_sidebar_collapsed = 'error';
        localStorageFlags.naka_sidebar_sections_collapsed = 'error';
      }

      event.contexts = event.contexts || {};
      event.contexts.hydrationDebug = {
        buildId: nextBuildId || 'unknown',
        publicBuildId: publicBuildId || 'unknown',
        timeZone,
        locale,
        timeZoneOffsetMinutes,
        localStorageFlags,
        documentHTML: document.documentElement.outerHTML.slice(0, 500),
        bodyClasses: document.body?.className || 'N/A',
        htmlAttributes: Array.from(document.documentElement.attributes)
          .map((a) => `${a.name}="${a.value}"`)
          .join(' '),
        bodyAttributes: document.body
          ? Array.from(document.body.attributes)
              .map((a) => `${a.name}="${a.value}"`)
              .join(' ')
          : 'N/A',
        headChildCount: document.head?.children.length || 0,
        bodyChildCount: document.body?.children.length || 0,
        scripts: Array.from(document.scripts)
          .slice(0, 10)
          .map((s) => s.src || '[inline]'),
        injectedElements: Array.from(
          document.querySelectorAll(
            '[data-grammarly], [data-darkreader], grammarly-desktop-integration, [class*="translate"]'
          )
        )
          .map((el) => el.tagName.toLowerCase())
          .slice(0, 10),
      };
      event.tags = event.tags || {};
      event.tags.isHydrationError = 'true';
      event.tags.buildId = nextBuildId || publicBuildId || 'unknown';
      event.tags.timeZone = timeZone;
      event.tags.possibleExtension = document.querySelector('[data-grammarly]')
        ? 'grammarly'
        : document.querySelector('[data-darkreader]')
          ? 'darkreader'
          : document.querySelector('html[class*="translated"]')
            ? 'google-translate'
            : 'unknown';
    }

    // Redact sensitive data from URLs
    if (event.request?.url) {
      event.request.url = redactSensitiveData(event.request.url);
    }

    // Redact sensitive data from headers
    if (event.request?.headers) {
      const headers = event.request.headers as Record<string, string>;
      for (const key of Object.keys(headers)) {
        if (/auth|token|cookie|session/i.test(key)) {
          headers[key] = '[REDACTED]';
        }
      }
    }

    // Redact sensitive data from breadcrumbs
    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
        if (breadcrumb.data?.url) {
          breadcrumb.data.url = redactSensitiveData(breadcrumb.data.url as string);
        }
        if (breadcrumb.message) {
          breadcrumb.message = redactSensitiveData(breadcrumb.message);
        }
        return breadcrumb;
      });
    }

    return event;
  },

  // Filter breadcrumbs to reduce noise
  beforeBreadcrumb(breadcrumb) {
    // Ignore noisy console logs
    if (breadcrumb.category === 'console' && breadcrumb.level === 'log') {
      return null;
    }

    // Redact fetch/xhr URLs that might contain tokens
    if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
      if (breadcrumb.data?.url) {
        breadcrumb.data.url = redactSensitiveData(breadcrumb.data.url as string);
      }
    }

    return breadcrumb;
  },

  // Ignore common errors that aren't actionable
  ignoreErrors: [
    // Browser extensions
    'top.GLOBALS',
    'originalCreateNotification',
    'canvas.contentDocument',
    'MyApp_RemoveAllHighlights',
    'http://tt.teletrax.com',
    'jigsaw is not defined',
    'ComboSearch is not defined',
    'atomicFindClose',

    // Facebook borance
    'fb_xd_fragment',

    // Chrome errors
    /^chrome:\/\//,

    // Safari errors
    /^Safari$/,
  ],

  denyUrls: [
    // Browser extensions
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
  ],
});
